import { FormEvent, useMemo, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import type { BOM, Item } from '../../services/types';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import EntityTypeaheadPicker, { type PickerOption } from '../../components/search/EntityTypeaheadPicker';

type ComponentDraft = {
    key: string;
    item_id: string;
    quantity: string;
    uom: string;
    remarks: string;
};

type ChildDraft = {
    key: string;
    description: string;
    quantity_per_parent: string;
    uom: string;
    dimensions: string;
    thickness: string;
    color: string;
    finish: string;
    planned_completion_date: string;
    remarks: string;
    components: ComponentDraft[];
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    items: Item[];
    initialBom?: BOM | null;
    mode?: 'create' | 'view' | 'edit';
}

const newKey = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const newComponent = (): ComponentDraft => ({
    key: newKey('component'),
    item_id: '',
    quantity: '1',
    uom: '',
    remarks: '',
});

const newChild = (): ChildDraft => ({
    key: newKey('child'),
    description: '',
    quantity_per_parent: '1',
    uom: 'Nos',
    dimensions: '',
    thickness: '',
    color: '',
    finish: '',
    planned_completion_date: '',
    remarks: '',
    components: [newComponent()],
});

const isFinishedGood = (item: Item): boolean => {
    const role = String(item.item_role || '').toUpperCase();
    const category = String(item.category || '').toLowerCase();
    const code = String(item.code || '').toUpperCase();
    return role === 'FG' || category.includes('finished') || code.startsWith('FG');
};

const isStockComponent = (item: Item): boolean => {
    const role = String(item.item_role || '').toUpperCase();
    const type = String(item.item_type || 'Stock').toLowerCase();
    return role !== 'FG' && type === 'stock';
};

const itemUom = (items: Item[], itemId: string) => (
    items.find((item) => String(item.id) === String(itemId))?.uom || ''
);

const recipeLabel = (bom?: BOM | null) => {
    if (!bom) return 'New Standard Recipe';
    return `${bom.bom_name || `Recipe ${bom.id}`} · Rev ${Number(bom.revision_no || 1)}`;
};

export function BOMEditor({ isOpen, onClose, onSuccess, items, initialBom = null, mode = 'create' }: Props) {
    const isViewOnly = mode === 'view';
    const [finishedGoodId, setFinishedGoodId] = useState(() => initialBom?.finished_good_id ? String(initialBom.finished_good_id) : '');
    const [bomName, setBomName] = useState(() => String(initialBom?.bom_name || ''));
    const [parentComponents, setParentComponents] = useState<ComponentDraft[]>(() => {
        const parentRows = (initialBom?.components || []).filter((component) => !component.recipe_line_id);
        return parentRows.length
            ? parentRows.map((component) => ({
                key: newKey('parent-component'),
                item_id: String(component.item_id || ''),
                quantity: String(component.quantity || 0),
                uom: String(component.uom || itemUom(items, String(component.item_id || '')) || ''),
                remarks: String(component.remarks || ''),
            }))
            : [newComponent()];
    });
    const [children, setChildren] = useState<ChildDraft[]>(() => {
        const structureLines = initialBom?.structure_lines || [];
        return structureLines.map((line) => {
            const childComponents = (initialBom?.components || []).filter((component) => Number(component.recipe_line_id || 0) === Number(line.recipe_line_id || 0));
            return {
                key: newKey('child'),
                description: String(line.description || ''),
                quantity_per_parent: String(line.quantity_per_parent || 1),
                uom: String(line.uom || 'Nos'),
                dimensions: String(line.dimensions || ''),
                thickness: String(line.thickness || ''),
                color: String(line.color || ''),
                finish: String(line.finish || ''),
                planned_completion_date: String(line.planned_completion_date || '').slice(0, 10),
                remarks: String(line.remarks || ''),
                components: childComponents.length
                    ? childComponents.map((component) => ({
                        key: newKey('child-component'),
                        item_id: String(component.item_id || ''),
                        quantity: String(component.quantity || 0),
                        uom: String(component.uom || itemUom(items, String(component.item_id || '')) || ''),
                        remarks: String(component.remarks || ''),
                    }))
                    : [newComponent()],
            };
        });
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const finishedGoodOptions = useMemo<PickerOption<string>[]>(() => (
        items.filter(isFinishedGood).map((item) => ({
            value: String(item.id),
            label: `${item.code} - ${item.name}`,
            description: item.category || 'Finished Good',
            badges: [{ label: item.uom || '-', tone: 'green' }],
        }))
    ), [items]);

    const componentOptions = useMemo<PickerOption<string>[]>(() => (
        items.filter(isStockComponent).map((item) => ({
            value: String(item.id),
            label: `${item.code} - ${item.name}`,
            description: item.category || 'Material / Accessory',
            badges: [
                { label: item.uom || '-', tone: 'slate' },
                { label: `Stock ${Number(item.current_stock || 0).toFixed(3)}`, tone: Number(item.current_stock || 0) > 0 ? 'green' : 'amber' },
            ],
        }))
    ), [items]);

    if (!isOpen) return null;

    const updateParentComponent = (key: string, patch: Partial<ComponentDraft>) => {
        setParentComponents((prev) => prev.map((row) => row.key === key ? { ...row, ...patch } : row));
    };

    const updateChild = (key: string, patch: Partial<ChildDraft>) => {
        setChildren((prev) => prev.map((row) => row.key === key ? { ...row, ...patch } : row));
    };

    const updateChildComponent = (childKey: string, componentKey: string, patch: Partial<ComponentDraft>) => {
        setChildren((prev) => prev.map((child) => child.key === childKey
            ? {
                ...child,
                components: child.components.map((component) => component.key === componentKey ? { ...component, ...patch } : component),
            }
            : child));
    };

    const componentPicker = (
        value: string,
        onChange: (patch: Partial<ComponentDraft>) => void,
    ) => (
        <EntityTypeaheadPicker
            value={value}
            options={componentOptions}
            placeholder="Search material/accessory..."
            emptyText="No matching materials"
            disabled={isViewOnly}
            onChange={(next) => onChange({ item_id: next, uom: itemUom(items, next) })}
        />
    );

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        const recipeName = bomName.trim();
        if (!recipeName) {
            toast.error('Recipe name is required');
            return;
        }
        const validParentComponents = parentComponents
            .map((component) => ({
                item_id: Number(component.item_id),
                quantity: Number(component.quantity || '0'),
                uom: component.uom.trim() || undefined,
                remarks: component.remarks.trim() || undefined,
            }))
            .filter((component) => component.item_id > 0 && component.quantity > 0);
        const structureLines = children
            .map((child, index) => ({
                recipe_line_id: -(index + 1),
                line_role: 'CHILD',
                description: child.description.trim(),
                quantity_per_parent: Number(child.quantity_per_parent || '0'),
                uom: child.uom.trim() || undefined,
                sort_order: index + 1,
                remarks: child.remarks.trim() || undefined,
                dimensions: child.dimensions.trim() || undefined,
                thickness: child.thickness.trim() || undefined,
                color: child.color.trim() || undefined,
                finish: child.finish.trim() || undefined,
                planned_completion_date: child.planned_completion_date || undefined,
            }))
            .filter((child) => child.description && child.quantity_per_parent > 0);
        const childComponents = children.flatMap((child, index) =>
            child.components
                .map((component) => ({
                    item_id: Number(component.item_id),
                    quantity: Number(component.quantity || '0'),
                    recipe_line_id: -(index + 1),
                    uom: component.uom.trim() || undefined,
                    remarks: component.remarks.trim() || undefined,
                }))
                .filter((component) => component.item_id > 0 && component.quantity > 0)
        );
        if (validParentComponents.length === 0 && childComponents.length === 0) {
            toast.error('Add at least one parent or child material/accessory');
            return;
        }
        const childWithoutMaterials = structureLines.find((line) => !childComponents.some((component) => component.recipe_line_id === line.recipe_line_id));
        if (childWithoutMaterials) {
            toast.error(`Add at least one material for ${childWithoutMaterials.description}`);
            return;
        }
        try {
            setIsSubmitting(true);
            const payload = {
                finished_good_id: finishedGoodId ? Number(finishedGoodId) : null,
                bom_name: recipeName,
                structure_lines: structureLines,
                components: [...validParentComponents, ...childComponents],
            };
            if (initialBom?.id && mode === 'edit') {
                await api.manufacturing.updateBom(initialBom.id, payload);
                toast.success('New recipe revision saved');
            } else {
                await api.manufacturing.createBom(payload);
                toast.success('Standard recipe saved');
            }
            onSuccess();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save standard recipe');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between border-b p-4">
                    <div>
                        <h2 className="text-lg font-semibold">{isViewOnly ? recipeLabel(initialBom) : mode === 'edit' ? `New Revision: ${recipeLabel(initialBom)}` : 'New Standard Recipe'}</h2>
                        <p className="text-xs text-slate-500">Recipe name is the production template. FG reference is optional for standard product catalog linkage.</p>
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={submit} className="space-y-5 overflow-y-auto p-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <RequiredLabel required htmlFor="recipe_name">Recipe Name</RequiredLabel>
                            <input
                                id="recipe_name"
                                className="mt-1 w-full rounded border border-slate-300 p-2 text-sm"
                                value={bomName}
                                onChange={(event) => setBomName(event.target.value)}
                                placeholder="e.g. Bike Delivery Box - Standard Build"
                                disabled={isViewOnly}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Standard FG Reference (optional)</label>
                            <div className="mt-1">
                                <EntityTypeaheadPicker
                                    value={finishedGoodId}
                                    options={finishedGoodOptions}
                                    placeholder="Search FG item..."
                                    emptyText="No matching finished goods"
                                    disabled={isViewOnly}
                                    onChange={setFinishedGoodId}
                                />
                            </div>
                            {!isViewOnly && finishedGoodId ? (
                                <button type="button" className="mt-1 text-xs text-slate-500 underline" onClick={() => setFinishedGoodId('')}>Clear FG reference</button>
                            ) : null}
                        </div>
                    </div>

                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-900">Parent Materials / Accessories</h3>
                            {!isViewOnly ? (
                                <button type="button" className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm" onClick={() => setParentComponents((prev) => [...prev, newComponent()])}>
                                    <Plus className="h-4 w-4" /> Add Material
                                </button>
                            ) : null}
                        </div>
                        <div className="space-y-2">
                            {parentComponents.map((component) => (
                                <div key={component.key} className="grid grid-cols-1 gap-2 rounded border p-2 md:grid-cols-[minmax(0,1fr)_110px_120px_minmax(0,1fr)_40px]">
                                    {componentPicker(component.item_id, (patch) => updateParentComponent(component.key, patch))}
                                    <input type="number" min="0" step="0.0001" className="rounded border border-slate-300 p-2 text-sm" value={component.quantity} onChange={(event) => updateParentComponent(component.key, { quantity: event.target.value })} disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 bg-slate-50 p-2 text-sm" value={component.uom} onChange={(event) => updateParentComponent(component.key, { uom: event.target.value })} placeholder="UOM" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={component.remarks} onChange={(event) => updateParentComponent(component.key, { remarks: event.target.value })} placeholder="Remarks" disabled={isViewOnly} />
                                    {!isViewOnly ? (
                                        <button type="button" className="rounded p-2 text-red-500 hover:bg-red-50" onClick={() => setParentComponents((prev) => prev.filter((row) => row.key !== component.key))}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    ) : <span />}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-900">Child / Sub-part Recipe Lines</h3>
                            {!isViewOnly ? (
                                <button type="button" className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm" onClick={() => setChildren((prev) => [...prev, newChild()])}>
                                    <Plus className="h-4 w-4" /> Add Sub-part
                                </button>
                            ) : null}
                        </div>
                        {children.map((child) => (
                            <div key={child.key} className="space-y-3 rounded border border-slate-200 p-3">
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-6">
                                    <input className="rounded border border-slate-300 p-2 text-sm md:col-span-2" value={child.description} onChange={(event) => updateChild(child.key, { description: event.target.value })} placeholder="Sub-part description" disabled={isViewOnly} />
                                    <input type="number" min="0" step="0.0001" className="rounded border border-slate-300 p-2 text-sm" value={child.quantity_per_parent} onChange={(event) => updateChild(child.key, { quantity_per_parent: event.target.value })} placeholder="Qty / parent" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={child.uom} onChange={(event) => updateChild(child.key, { uom: event.target.value })} placeholder="UOM" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={child.dimensions} onChange={(event) => updateChild(child.key, { dimensions: event.target.value })} placeholder="Dimensions" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={child.thickness} onChange={(event) => updateChild(child.key, { thickness: event.target.value })} placeholder="Thickness" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={child.color} onChange={(event) => updateChild(child.key, { color: event.target.value })} placeholder="Color" disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm" value={child.finish} onChange={(event) => updateChild(child.key, { finish: event.target.value })} placeholder="Finish" disabled={isViewOnly} />
                                    <input type="date" className="rounded border border-slate-300 p-2 text-sm" value={child.planned_completion_date} onChange={(event) => updateChild(child.key, { planned_completion_date: event.target.value })} disabled={isViewOnly} />
                                    <input className="rounded border border-slate-300 p-2 text-sm md:col-span-2" value={child.remarks} onChange={(event) => updateChild(child.key, { remarks: event.target.value })} placeholder="Remarks" disabled={isViewOnly} />
                                    {!isViewOnly ? (
                                        <button type="button" className="rounded p-2 text-red-500 hover:bg-red-50" onClick={() => setChildren((prev) => prev.filter((row) => row.key !== child.key))}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    ) : null}
                                </div>
                                <div className="space-y-2">
                                    {child.components.map((component) => (
                                        <div key={component.key} className="grid grid-cols-1 gap-2 pl-3 md:grid-cols-[minmax(0,1fr)_110px_120px_minmax(0,1fr)_40px]">
                                            {componentPicker(component.item_id, (patch) => updateChildComponent(child.key, component.key, patch))}
                                            <input type="number" min="0" step="0.0001" className="rounded border border-slate-300 p-2 text-sm" value={component.quantity} onChange={(event) => updateChildComponent(child.key, component.key, { quantity: event.target.value })} disabled={isViewOnly} />
                                            <input className="rounded border border-slate-300 bg-slate-50 p-2 text-sm" value={component.uom} onChange={(event) => updateChildComponent(child.key, component.key, { uom: event.target.value })} placeholder="UOM" disabled={isViewOnly} />
                                            <input className="rounded border border-slate-300 p-2 text-sm" value={component.remarks} onChange={(event) => updateChildComponent(child.key, component.key, { remarks: event.target.value })} placeholder="Remarks" disabled={isViewOnly} />
                                            {!isViewOnly ? (
                                                <button type="button" className="rounded p-2 text-red-500 hover:bg-red-50" onClick={() => updateChild(child.key, { components: child.components.filter((row) => row.key !== component.key) })}>
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            ) : <span />}
                                        </div>
                                    ))}
                                    {!isViewOnly ? (
                                        <button type="button" className="inline-flex items-center gap-1 pl-3 text-sm font-medium text-blue-600" onClick={() => updateChild(child.key, { components: [...child.components, newComponent()] })}>
                                            <Plus className="h-3 w-3" /> Add sub-part material
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                        {children.length === 0 ? (
                            <div className="rounded border border-dashed p-3 text-sm text-slate-500">No child/sub-part lines. Parent materials will apply directly to the selected Work Order part.</div>
                        ) : null}
                    </section>

                    <div className="flex justify-end gap-2 border-t pt-4">
                        <button type="button" onClick={onClose} className="rounded px-4 py-2 text-sm text-slate-700 hover:bg-slate-100">Close</button>
                        {!isViewOnly ? (
                            <button type="submit" disabled={isSubmitting} className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                                {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Save New Revision' : 'Create Recipe'}
                            </button>
                        ) : null}
                    </div>
                </form>
            </div>
        </div>
    );
}
